import React, { useEffect, useState } from 'react'
import { Modal, Form, Radio, TreeSelect, Space, Button, Flex } from 'antd'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useWatch } from 'antd/es/form/Form'
import styled from 'styled-components'
import { TrendingUp } from 'lucide-react'
import { contextCompanyIdSelector } from '../../../redux/context/company/selectors'
import { filtersSelector } from '../../../redux/context/filters/selectors'
import { EnhancedStatementRow } from '../../financialStatements/utils/dataEnhancement'
import { useUpdateBudgetingMethod } from '../hooks/useUpdateBudgetingMethod'
import { BudgetingMethodType, BudgetingMethodData } from '../types'
import AccountTreeSelect from '../../account/components/AccountTreeSelect'
import DriverSource from './DriverSource'
import CtxDimensionTreeSelectWithRoot from '../../dimension/components/CtxDimensionTreeSelectWithRoot'
import { useNavigateToDriverData } from '../hooks/useNavigateToDriverData'
import { parseArrayFromString } from '../../../shared/utils'

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const Separator = styled.span`
  color: #d9d9d9;
  margin: 0 4px;
`

interface BudgetMethodModalProps {
  visible: boolean
  row?: EnhancedStatementRow
  onClose: () => void
  onSuccess: () => void
}

interface BudgetMethodFormValues {
  method: BudgetingMethodType
  source?: string
  sourceIds?: (number | null)[] // AccountTreeSelect handles group expansion automatically
  dimensionIds?: (string | null)[]
}

const BudgetMethodModal: React.FC<BudgetMethodModalProps> = ({ visible, row, onClose, onSuccess }) => {
  const { t } = useTranslation()
  const [form] = Form.useForm<BudgetMethodFormValues>()
  const companyId = useSelector(contextCompanyIdSelector)
  const { dimensions: contextDimensions } = useSelector(filtersSelector)
  const { mutateAsync: updateBudgetingMethod, isPending, error: requestError } = useUpdateBudgetingMethod()
  // const [requestError, setRequestError] = useState<any>({ source: '', error: null })
  const navigateToDriverData = useNavigateToDriverData()

  // Store the source value at the time of error
  const [errorSource, setErrorSource] = useState<string>('')

  // Parse the error from React Query format to FormulaErrorType
  const parsedError = React.useMemo(() => {
    if (!requestError) return null

    try {
      // React Query error typically has the response data in error.data or error.response.data
      const errorData = (requestError as any)?.data || (requestError as any)?.response?.data

      if (!errorData) return null

      // If message is a JSON string, parse it
      if (typeof errorData.message === 'string') {
        return JSON.parse(errorData.message)
      }

      // If it's already an object, return it directly
      return errorData
    } catch (e) {
      console.error('Failed to parse error:', e)
      return null
    }
  }, [requestError])

  // Watch current method selection
  const budgetingMethod = useWatch('method', form)

  // Reset form when modal opens with new row
  useEffect(() => {
    if (visible && row) {
      // Use context dimensions if available, otherwise use null (enterprise level)
      const defaultDimensionIds = contextDimensions && contextDimensions.length > 0 ? contextDimensions : ['null']

      form.setFieldsValue({
        method: row.budgetingMethod?.method || 'absolute',
        source: row.budgetingMethod?.source,
        sourceIds: parseArrayFromString(row.budgetingMethod?.source),
        // Use context dimensions as default
        dimensionIds: defaultDimensionIds
      })
      // Clear error source when modal opens
      setErrorSource('')
    }
  }, [visible, row, form, contextDimensions])

  const handleSubmit = async () => {
    if (!row || !companyId) return

    try {
      const formFields = await form.validateFields()
      const { method, sourceIds, dimensionIds, source } = formFields

      let budgetingMethodData: BudgetingMethodData = {
        accountCode: row._sourceAccountCode,
        method,
        source: null // Clear source by default
      }

      // Add reference-specific data
      if (method === 'reference' && sourceIds) {
        // sourceIds are already expanded by AccountTreeSelect
        // Filter out any null values (shouldn't happen for accounts, but safety check)
        const validSourceIds = sourceIds.filter(id => id !== null && id !== undefined)
        budgetingMethodData.source = `account([${validSourceIds.join()}], current)`
      }

      // Add driver-specific data
      if (method === 'driver') {
        // Normalize dimensionIds: convert "null" strings to null
        const normalizedDimensionIds = dimensionIds?.map(id => (id === 'null' ? null : id))

        budgetingMethodData = {
          ...budgetingMethodData,
          source,
          dimensionIds: normalizedDimensionIds
        }

        // Store the source value for error display
        setErrorSource(source || '')
      }

      await updateBudgetingMethod(budgetingMethodData)

      setErrorSource('') // Clear error source on success
      onSuccess()
      onClose()
    } catch (error: any) {
      console.error('Error updating budgeting method:', error)
    }
  }

  // Initialize form values when method changes
  useEffect(() => {
    if (!budgetingMethod || !row?.budgetingMethod) return

    // Use context dimensions if available, otherwise use null (enterprise level)
    const defaultDimensionIds = contextDimensions && contextDimensions.length > 0 ? contextDimensions : ['null']

    const currentMethod = row.budgetingMethod.method
    if (currentMethod === 'absolute') {
      if (budgetingMethod === 'driver') {
        // Switching from absolute to driver - set default dimensions
        form.setFieldsValue({ source: '', dimensionIds: defaultDimensionIds })
      } else {
        form.setFieldsValue({ source: '', dimensionIds: undefined })
      }
    } else if (currentMethod === 'reference') {
      if (budgetingMethod === 'driver') {
        // Switching from reference to driver - set default dimensions
        form.setFieldsValue({ source: '', dimensionIds: defaultDimensionIds })
      } else if (budgetingMethod === 'absolute') {
        form.setFieldsValue({ source: '' })
      } else if (budgetingMethod === 'reference') {
        form.setFieldsValue({
          source: row.budgetingMethod.source,
          dimensionIds: undefined
        })
      }
    } else if (currentMethod === 'driver') {
      if (budgetingMethod === 'reference' || budgetingMethod === 'absolute') {
        form.setFieldsValue({ source: '' })
      } else if (budgetingMethod === 'driver') {
        form.setFieldsValue({ source: row.budgetingMethod.source })
      }
    }
  }, [budgetingMethod, row, form, contextDimensions])

  const handleCancel = () => {
    form.resetFields()
    setErrorSource('') // Clear error source on cancel
    onClose()
  }

  // Generate modal title
  const getTitle = () => {
    if (!row) {
      return (
        <TitleRow>
          <TrendingUp size={18} color="#8c8c8c" strokeWidth={1.5} />
          <span>{t('financialStatementsPage:budgeting-method')}</span>
        </TitleRow>
      )
    }

    const parts: React.ReactNode[] = []

    // Add account name or code
    if (row.name) {
      parts.push(<span key="account">{`${row._sourceAccountCode} - ${row.name}`}</span>)
    } else if (row._sourceAccountCode) {
      parts.push(<span key="account">{row._sourceAccountCode}</span>)
    }

    // Join parts with separator
    const content = parts.reduce<React.ReactNode[]>((acc, part, index) => {
      if (index > 0) {
        // eslint-disable-next-line react/no-array-index-key
        acc.push(<Separator key={`sep-${index}`}>•</Separator>)
      }
      acc.push(part)
      return acc
    }, [])

    return (
      <TitleRow>
        <TrendingUp size={18} color="#8c8c8c" strokeWidth={1.5} />
        <span>{t('financialStatementsPage:budgeting-method')}</span>
        {content.length > 0 && <Separator>•</Separator>}
        {content}
      </TitleRow>
    )
  }

  return (
    <Modal
      title={getTitle()}
      open={visible}
      onCancel={handleCancel}
      onOk={handleSubmit}
      confirmLoading={isPending}
      okText={t('global:save')}
      cancelText={t('global:cancel')}
      destroyOnHidden
      maskClosable={false}
      footer={
        <Flex justify="space-between">
          <div>
            {budgetingMethod === 'driver' && (
              <Button color="primary" variant="filled" onClick={() => navigateToDriverData(row)}>
                {t('financialStatementsPage:navigate-to-driver-data')}
              </Button>
            )}
          </div>
          <Space>
            <Button onClick={handleCancel}>{t('global:cancel')}</Button>
            <Button loading={isPending} onClick={handleSubmit} type="primary">
              {t('global:save')}
            </Button>
          </Space>
        </Flex>
      }
    >
      <Form form={form} layout="vertical">
        <Form.Item name="method">
          <Radio.Group>
            <Space direction="vertical">
              <Radio value="absolute">{t('financialStatementsPage:budgetmenu-absolute')}</Radio>
              <Radio value="reference">{t('financialStatementsPage:budgetmenu-reference')}</Radio>
              <Radio value="driver">{t('financialStatementsPage:budgetmenu-budget-driver')}</Radio>
            </Space>
          </Radio.Group>
        </Form.Item>

        {/* Reference method - Source selection */}
        {budgetingMethod === 'reference' && (
          <Form.Item
            name="sourceIds"
            rules={[{ required: true, message: t('global:required-field') }]}
            label={t('financialStatementsPage:budget-reference-select')}
          >
            <AccountTreeSelect
              multiple
              allowClear
              treeCheckable
              showCheckedStrategy={TreeSelect.SHOW_PARENT}
              maxTagCount={5}
            />
          </Form.Item>
        )}

        {/* Driver method - Budget driver configuration */}
        {budgetingMethod === 'driver' && (
          <>
            <DriverSource form={form} requestError={parsedError} errorSourceValue={errorSource} />
            <Form.Item label={t('financialStatementsPage:add-formula-to-dimensions')} name="dimensionIds">
              <CtxDimensionTreeSelectWithRoot
                // showCheckedStrategy={TreeSelect.SHOW_ALL}
                style={{ width: '100%' }}
                maxTagCount={20}
                // treeCheckable
                showSearch
                multiple
              />
            </Form.Item>
          </>
        )}
      </Form>
    </Modal>
  )
}

export default BudgetMethodModal
